import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Dropdown as SemanticDropdown } from 'semantic-ui-react';
import { FastField } from 'formik';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import classNames from 'classnames';
import './Dropdown.css';

export class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.handleSearchChange = debounce(this.handleSearchChange, 300);
  }

  state = {
    isFetching: false,
    localOptions: [],
  };

  handleBlur = (e, { name, value }, form) => form.setFieldTouched(name, value);

  handleChange = (e, field, form) => {
    form.setFieldValue(field.name, field.value);
    this.props.onChange(field, form);
  };

  getInitialValue = ({ value }) =>
    value ? [{ key: value, text: value, value }] : [];

  componentDidMount() {
    this.handleSearchChange(null, { searchQuery: '' });
  }

  handleSearchChange = (e, { searchQuery }) => {
    if (!this.props.search) return;
    this.setState({ isFetching: true });
    this.props
      .search(searchQuery)
      .then(response => {
        const localOptions = map(response.data, ({ name }) => ({
          key: name,
          text: name,
          value: name,
        }));

        this.setState({ isFetching: false, localOptions });
      })
      .catch(() => this.setState({ isFetching: false }));
  };

  handleAddition = (e, { value }) => {
    this.setState({
      localOptions: [{ text: value, value }, ...this.state.localOptions],
    });
  };

  render() {
    const {
      allowAdditions,
      name,
      options,
      label,
      icon,
      placeholder,
      containerClassName,
      labelClassName,
      dropdownClassName,
      search,
      noResultsMessage,
      showError,
      fluid,
      enableSearch,
      value,
      extraClassName,
      oldStyle,
      hasInput,
      multiple,
    } = this.props;

    const { localOptions, isFetching } = this.state;

    if (isFetching) return <div />;

    return (
      <FastField
        name={name}
        render={({ field, form }) => {
          const initialValue = field.value
            ? this.getInitialValue(field)
            : this.getInitialValue({ value });

          field.value = field.value || value;
          const allOptionsForFilter = [...options, ...localOptions]
          const mergedOptions = uniqBy(allOptionsForFilter, 'key');
          const error = get(form.errors, field.name);
          const touched = get(form.touched, field.name);
          const showFieldError = showError && touched && error;
          return oldStyle ? (
            <div className={`${containerClassName} ${extraClassName}`}>
              {!isEmpty(label) && (
                <label
                  className={classNames(
                    labelClassName,
                    'text-gray-light t-14',
                    showFieldError && 'input__label-error',
                  )}
                >
                  {label}
                </label>
              )}
              <div
                className={classNames('dropdown', fluid && 'dropdown__block')}
              >
                <SemanticDropdown
                  multiple={multiple}
                  fluid={fluid}
                  selection
                  error={showFieldError}
                  allowAdditions={allowAdditions}
                  noResultsMessage={noResultsMessage}
                  placeholder={placeholder}
                  icon={icon}
                  name={field.name}
                  value={field.value}
                  search={enableSearch}
                  onSearchChange={search && this.handleSearchChange}
                  onAddItem={this.handleAddition}
                  onChange={(e, data) => this.handleChange(e, data, form)}
                  onBlur={(e, data) => this.handleBlur(e, data, form)}
                  options={mergedOptions}
                  className={classNames('form-control', dropdownClassName)}
                  loading={isFetching}
                />
                {showFieldError && (
                  <div className="dropdown__error">{error}</div>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`${containerClassName} ${extraClassName} ${hasInput &&
                'has-input'}`}
            >
              <span className="input-label text-gray-light">{label}</span>
              <div
                className={classNames('dropdown', fluid && 'dropdown__block')}
              >
                <SemanticDropdown
                  fluid={fluid}
                  multiple={multiple}
                  onLabelClick={e => {
                    const leftOptions = field.value.filter(
                      val =>
                        options.find(opt => opt.value === val).text !==
                        e.target.text,
                    );
                    this.handleChange(
                      e,
                      { name: field.name, value: leftOptions },
                      form,
                    );
                  }}
                  selection
                  error={showFieldError}
                  allowAdditions={allowAdditions}
                  noResultsMessage={noResultsMessage}
                  placeholder={placeholder}
                  icon={icon}
                  name={field.name}
                  value={field.value}
                  search={enableSearch}
                  onSearchChange={search && this.handleSearchChange}
                  onAddItem={this.handleAddition}
                  onChange={(e, data) => this.handleChange(e, data, form)}
                  onBlur={(e, data) => this.handleBlur(e, data, form)}
                  options={mergedOptions}
                  className={classNames('form-control', dropdownClassName)}
                  loading={isFetching}
                />
                {showFieldError && (
                  <div className="dropdown__error">{error}</div>
                )}
              </div>
            </div>
          );
        }}
      />
    );
  }
}

Dropdown.defaultProps = {
  icon: 'chevron down',
  noResultsMessage: 'No results found.',
  options: [],
  onChange: () => {},
  showError: true,
};

Dropdown.propTypes = {
  allowAdditions: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  search: PropTypes.func,
  noResultsMessage: PropTypes.string,
  onChange: PropTypes.func,
  showError: PropTypes.bool,
  fluid: PropTypes.bool,
  enableSearch: PropTypes.bool,
  extraClassName: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/components/Form/Dropdown/Dropdown.js